/**
 * SchedulingWidget Component
 * 
 * Reusable scheduling component that handles date/time selection, timezone display,
 * conflict detection, and quick schedule options. Used by both PostActions and RescheduleDialog.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, 
    Stack,
    Tooltip,
    CircularProgress,
    InputAdornment,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Warning as WarningIcon,
    Language as LanguageIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { styled } from '@mui/material/styles';
import QuickSchedule from './QuickSchedule';
import ConflictWarning from './ConflictWarning';
import { 
    checkConflictsAPI, 
    CONFLICT_WINDOWS,
    getUserTimezone
} from '../../utils/schedulingHelpers';
import { useAuth } from '../../providers/AuthProvider';
import { useTracking } from '../../hooks/useTracking';
import { EnhancedPlatformEvents } from '../../services/analytics/events';

// Extend dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

// Styled components
const ScheduleContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        width: '100%'
    }
}));

const TimezoneChip = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
    height: 24
}));

const SchedulingWidget = ({
    postDate,
    onDateChange,
    disabled = false,
    timezoneInfo,
    scheduleValidation,
    selectedPlatforms = [],
    postContent = '',
    editPostId = null,
    showQuickSchedule = true,
    showConflictDetection = true,
    isCompact = false,
    label = "Schedule Date & Time",
    minDateTime = null
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user, token } = useAuth();
    const { trackEvent } = useTracking();
    
    // Conflict detection state
    const [conflictData, setConflictData] = useState(null);
    const [checkingConflicts, setCheckingConflicts] = useState(false);
    const [lastCheckedTime, setLastCheckedTime] = useState(null);
    
    // Get user's conflict window preference from profile, fallback to default
    const conflictWindow = user?.[0]?.userprofile?.conflict_window || CONFLICT_WINDOWS.DEFAULT;

    // Check for conflicts when date changes
    const checkConflicts = useCallback(async (dateValue) => {
        if (!dateValue || !token || !showConflictDetection) return;

        // Don't re-check if it's the same time
        if (lastCheckedTime === dateValue) return;

        setCheckingConflicts(true);
        setLastCheckedTime(dateValue);

        try {
            const result = await checkConflictsAPI({
                scheduledTime: dateValue,
                conflictWindow,
                excludePostId: editPostId,
                platforms: selectedPlatforms,
                postText: postContent,
                token
            });

            setConflictData(result);

            // Track conflict detection
            if (result.has_conflicts || result.has_duplicate_content) {
                trackEvent(EnhancedPlatformEvents.SCHEDULING_CONFLICT_DETECTED.name, {
                    conflictCount: result.conflict_count,
                    hasDuplicates: result.has_duplicate_content,
                    conflictWindow: conflictWindow,
                    platforms: selectedPlatforms
                });
            }
        } catch (error) {
            console.error('Error checking conflicts:', error);
            // Don't show error to user, just silently fail conflict checking
            setConflictData(null);
        } finally {
            setCheckingConflicts(false);
        }
    }, [token, conflictWindow, editPostId, selectedPlatforms, postContent, lastCheckedTime, trackEvent, showConflictDetection]);

    // Check conflicts when date changes
    useEffect(() => {
        if (postDate && showConflictDetection) {
            const debounceTimer = setTimeout(() => {
                checkConflicts(postDate);
            }, 500); // Debounce to avoid too many API calls

            return () => clearTimeout(debounceTimer);
        }
    }, [postDate, checkConflicts, showConflictDetection]);

    // Handle date change with conflict checking
    const handleDateChange = (newValue) => {
        onDateChange(newValue);
    };

    // Handle quick schedule selection
    const handleQuickSchedule = (scheduledTime) => {
        handleDateChange(scheduledTime);
        trackEvent(EnhancedPlatformEvents.QUICK_SCHEDULE_SELECTED.name, {
            selectedTime: scheduledTime,
            source: 'quick_schedule'
        });
    };

    // Handle conflict suggestion selection
    const handleSuggestionSelect = (suggestion) => {
        const newDate = dayjs(suggestion.time).format('YYYY-MM-DDTHH:mm');
        handleDateChange(newDate);
        
        // Clear conflicts after selecting suggestion
        setConflictData(null);
    };

    // Handle refresh suggestions
    const handleRefreshSuggestions = () => {
        if (postDate) {
            checkConflicts(postDate);
        }
    };

    // Get user timezone
    const userTimezone = getUserTimezone(user?.[0]?.userprofile);

    // Determine if we should show warnings
    const hasConflicts = conflictData?.has_conflicts || conflictData?.has_duplicate_content;
    const showConflictWarning = hasConflicts && !checkingConflicts && showConflictDetection;

    // Create fallback timezone display if timezoneInfo is null or invalid
    const getTimezoneDisplay = () => {
        if (timezoneInfo && timezoneInfo.displayText) {
            return timezoneInfo;
        }
        
        // Fallback: create basic timezone info
        const fallbackTz = userTimezone || 'UTC';
        const now = new Date();
        let displayText;
        
        try {
            displayText = new Intl.DateTimeFormat('en', {
                timeZone: fallbackTz,
                timeZoneName: 'short'
            }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value || fallbackTz;
        } catch (error) {
            displayText = fallbackTz;
        }
        
        return {
            displayText,
            isDifferent: false
        };
    };

    const displayTimezone = getTimezoneDisplay();

    return (
        <Box>
            {/* Conflict Warning */}
            {showConflictWarning && conflictData && (
                <ConflictWarning
                    conflicts={conflictData.conflicts || []}
                    duplicateConflicts={conflictData.duplicate_conflicts || []}
                    suggestions={conflictData.suggestions || []}
                    conflictWindow={conflictWindow}
                    onSelectSuggestion={handleSuggestionSelect}
                    onRefreshSuggestions={handleRefreshSuggestions}
                    isLoading={checkingConflicts}
                />
            )}
            
            <ScheduleContainer>
                <Stack spacing={isCompact ? 1 : 2}>
                    {/* Quick Schedule Options */}
                    {showQuickSchedule && !postDate && (
                        <QuickSchedule
                            onSelectTime={handleQuickSchedule}
                            selectedPlatforms={selectedPlatforms}
                            disabled={disabled}
                        />
                    )}

                    {/* Date Time Picker with Timezone */}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <DateTimePicker
                                label={label}
                                value={postDate ? dayjs(postDate) : null}
                                onChange={handleDateChange}
                                disabled={disabled}
                                minDateTime={minDateTime || dayjs().add(3, 'minute')}
                                format="MM/DD/YYYY hh:mm A"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: scheduleValidation && !scheduleValidation.isValid,
                                        helperText: scheduleValidation?.message,
                                        size: isCompact ? 'small' : 'medium',
                                        InputProps: {
                                            startAdornment: checkingConflicts && showConflictDetection && (
                                                <InputAdornment position="start">
                                                    <CircularProgress size={16} />
                                                </InputAdornment>
                                            )
                                        }
                                    }
                                }}
                            />
                            {/* Timezone Display */}
                            {displayTimezone && !isCompact && (
                                <Tooltip title={displayTimezone.isDifferent 
                                    ? "Times shown in profile timezone" 
                                    : "Times shown in your timezone"
                                }>
                                    <TimezoneChip>
                                        <LanguageIcon sx={{ fontSize: 14 }} />
                                        {displayTimezone.displayText}
                                    </TimezoneChip>
                                </Tooltip>
                            )}
                        </Box>
                    </LocalizationProvider>

                    {/* Schedule Validation Warning */}
                    {scheduleValidation && !scheduleValidation.isValid && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            color: 'error.main',
                            fontSize: '0.875rem'
                        }}>
                            <WarningIcon fontSize="small" />
                            {scheduleValidation.message}
                        </Box>
                    )}
                </Stack>
            </ScheduleContainer>
        </Box>
    );
};

export default SchedulingWidget; 